import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ocrExtract,
  ocrExtractStream,
  ocrCopy,
  ocrAlign
} from "../api/ocr";
import "./ocrExtractView.css";
import StreamlitTableRenderer from "./StreamlitTableRenderer";
import applyEditsToJson, { normalizeImportanceLabels } from "./applyEditsToJson";
import { getPageCount } from "../utils/pageCounter";
import { emitCreditUpdated } from "../utils/creditEvent";
import PdfMaskPreview from "../components/PdfMaskPreview";
import { API_ENDPOINTS } from "./../api/endpoints";
import http from "../api/http";
import { downloadCsvFromJson } from "../utils/exportCsv";
import { createPortal } from "react-dom";
import { useLanguage } from "../context/LanguageContext";

const IMPORTANCE_ADMIN = "\u7ba1\u7406\u7528";
const IMPORTANCE_CORE = "\u30b3\u30a2";
const IMPORTANCE_SUPPLEMENTARY = "\u88dc\u52a9\u7528";
const IMPORTANCE_LEVELS = [IMPORTANCE_ADMIN, IMPORTANCE_CORE, IMPORTANCE_SUPPLEMENTARY];
function getFileType(file) {
  if (!file) return null;
  if (file.type === "application/pdf") return "pdf";
  if (file.type.startsWith("image/")) return "image";
  return "unknown";
}

function filterByImportance(data, filters) {
  if (!data) return null;

  // Array case
  if (Array.isArray(data)) {
    return data
      .map((item) => filterByImportance(item, filters))
      .filter(Boolean);
  }

  // Object case
  if (typeof data === "object") {
    const importanceValue = data.importance || data["重要度"];
    const filterKey = {
      core: "コア",
      supplementary: "補助用",
      administrative: "管理用",
    }[importanceValue] || importanceValue;

    // Remove if importance not selected
    if (filterKey && !filters[filterKey]) {
      return null;
    }

    // Recursively filter children
    const filteredObj = {};
    for (const key in data) {
      const result = filterByImportance(data[key], filters);
      if (result !== null) {
        filteredObj[key] = result;
      }
    }

    // ✅ Treat empty 値 as no value
    const hasValue =
      "値" in filteredObj &&
      String(filteredObj["値"] || "").trim() !== "";

    // Metadata-only object
    const onlyMeta = Object.keys(filteredObj).every(
      (k) => k === "重要度" || k === "field_id" || k === "key_jp"
    );

    // Remove importance-only empty objects
    if (!hasValue && onlyMeta) {
      return null;
    }

    // Remove empty folders
    if (Object.keys(filteredObj).length === 0) {
      return null;
    }

    return filteredObj;
  }

  return data;
}

// Preserve empty fields when filtering a reusable template schema.
function filterTemplateSchemaByImportance(data, filters) {
  if (Array.isArray(data)) {
    const filteredItems = data
      .map((item) => filterTemplateSchemaByImportance(item, filters))
      .filter((item) => item !== null);
    return filteredItems.length > 0 ? filteredItems : null;
  }
  if (!data || typeof data !== "object") return data;

  const isLeaf =
    Object.prototype.hasOwnProperty.call(data, "value") ||
    Object.prototype.hasOwnProperty.call(data, "値");
  const importanceValue = data.importance || data["重要度"];
  const filterKey = {
    core: IMPORTANCE_CORE,
    supplementary: IMPORTANCE_SUPPLEMENTARY,
    administrative: IMPORTANCE_ADMIN,
  }[importanceValue] || importanceValue;
  if (isLeaf) {
    return !filterKey || filters[filterKey] ? { ...data } : null;
  }

  const filtered = {};
  Object.entries(data).forEach(([key, value]) => {
    if (!value || typeof value !== "object") return;
    const child = filterTemplateSchemaByImportance(value, filters);
    if (child !== null) filtered[key] = child;
  });

  if (Object.keys(filtered).length === 0) return null;
  // Primitive properties here are metadata (page, key_jp, field_id, etc.).
  // Keep them only after proving that the container still has selected leaves.
  Object.entries(data).forEach(([key, value]) => {
    if (!value || typeof value !== "object") filtered[key] = value;
  });
  return filtered;
}
function normalizeExtractContext(rawContext) {
  if (rawContext?.pages && Array.isArray(rawContext.pages)) {
    const merged = {};

    for (const p of rawContext.pages) {
      const pageNo = p?.page ?? "?";

      if (p?.context && typeof p.context === "object") {
        const ctxKeys = Object.keys(p.context);

        if (ctxKeys.length === 1 && ctxKeys[0].startsWith("Page")) {
          merged[`Page ${pageNo}`] = p.context[ctxKeys[0]];
        } else {
          merged[`Page ${pageNo}`] = p.context;
        }
      } else {
        merged[`Page ${pageNo}`] = {};
      }
    }

    return normalizeImportanceLabels(merged);
  }

  return normalizeImportanceLabels(rawContext);
}

function sanitizeDownloadBaseName(name) {
  const normalized = String(name || "context")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/[. ]+$/g, "");
  return normalized || "context";
}

function clearTemplateExtractedValues(data) {
  if (Array.isArray(data)) {
    return data.map(clearTemplateExtractedValues);
  }

  if (!data || typeof data !== "object") {
    return data;
  }

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (
        key === "値" ||
        key === "value" ||
        key === "extracted_value" ||
        key === "extractedValue"
      ) {
        return [key, null];
      }

      return [key, clearTemplateExtractedValues(value)];
    })
  );
}
function toUserFacingOcrError(errorLike) {
  const rawMessage = String(
    errorLike?.response?.data?.message ||
    errorLike?.message ||
    errorLike?.error ||
    errorLike ||
    ""
  );
  // The stream endpoint returns its JSON error body as the message text.
  let parsed = null;
  try { parsed = JSON.parse(rawMessage); } catch (_) { parsed = null; }
  const technicalMessage = parsed?.message ? String(parsed.message) : rawMessage;
  const errorCode = String(
    errorLike?.response?.data?.error || errorLike?.error || parsed?.error || ""
  );

  if (/account has expired/i.test(technicalMessage)) {
    return {
      title: "ご契約の有効期限が切れています",
      message: "「プラン・請求」画面で、更新請求書のお支払い（支払期限内ならクレジットを引き継いで再開できます）またはプランのお申し込みをお願いします。",
      retryable: false,
    };
  }

  if (
    errorCode === "OcrServiceUnavailable" ||
    errorCode === "OcrServiceTimeout" ||
    /ECONNREFUSED|fetch failed|Network Error/i.test(technicalMessage)
  ) {
    return {
      title: "OCRサービスを利用できません",
      message: "OCRサービスが一時的に停止しています。しばらくしてから、もう一度お試しください。",
      retryable: true,
    };
  }

  return {
    title: "抽出を完了できませんでした",
    message: technicalMessage || "時間をおいて、もう一度お試しください。",
    retryable: true,
  };
}

function OcrErrorNotice({ error, onRetry, text }) {
  const notice =
    error && typeof error === "object" && error.title
      ? error
      : toUserFacingOcrError(error);

  return (
    <div className="ocr-error-notice" role="alert">
      <div className="ocr-error-notice__icon" aria-hidden="true">!</div>
      <div className="ocr-error-notice__content">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      {notice.retryable && (
        <button type="button" className="ocr-error-notice__retry" onClick={onRetry}>
          {text("再試行", "Retry")}
        </button>
      )}
    </div>
  );
}

function getModeIdFromSavedBoxResult(saved) {
  if (saved?.modeId) {
    return saved.modeId;
  }

  const plan = String(saved?.plan || "").trim().toLowerCase();

  if (plan === "fast") {
    return "mode_fast";
  }

  if (plan === "premium" || plan === "high_quality" || plan === "high quality") {
    return "mode_high_quality";
  }

  return "mode_standard";
}


/** Convert pixel rect -> relative rect (0..1) using known drawing surface size */
function toRelRect(pxRect, surfaceW, surfaceH) {
  if (!pxRect) return null;

  // ✅ If already relative (from new responsive logic), return directly
  if (pxRect.isRelative) {
    return {
      rel_left: pxRect.x,
      rel_top: pxRect.y,
      rel_width: pxRect.w,
      rel_height: pxRect.h,
    };
  }

  if (surfaceW <= 0 || surfaceH <= 0) return null;

  const x1 = Math.max(0, Math.min(surfaceW, pxRect.x));
  const y1 = Math.max(0, Math.min(surfaceH, pxRect.y));
  const x2 = Math.max(0, Math.min(surfaceW, pxRect.x + pxRect.w));
  const y2 = Math.max(0, Math.min(surfaceH, pxRect.y + pxRect.h));

  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const right = Math.max(x1, x2);
  const bottom = Math.max(y1, y2);

  const w = right - left;
  const h = bottom - top;

  if (w < 2 || h < 2) return null;

  return {
    rel_left: left / surfaceW,
    rel_top: top / surfaceH,
    rel_width: w / surfaceW,
    rel_height: h / surfaceH,
  };
}

export default function OcrExtractView() {
  const { text } = useLanguage();
  const [file, setFile] = useState(null);
  const [previewDrag, setPreviewDrag] = useState(false);
  const [modeId, setModeId] = useState("mode_standard");
  const [preprocess, setPreprocess] = useState(false);
  const [uiVisibility, setUiVisibility] = useState({
    showFast: false,
    showImagePreprocessing: false,
  });
  const [loading, setLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState("");
  const streamAccumulated = useRef("");
  const lastStreamBlockCount = useRef(0);
  const [error, setError] = useState(null);
  const [creditPopup, setCreditPopup] = useState(null);
  const [contextJson, setContextJson] = useState(null);
  const [edits, setEdits] = useState({});

  const [expandPreview, setExpandPreview] = useState(false);
  const [expandResult, setExpandResult] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [saveMsg, setSaveMsg] = useState("");
  // ✅ Importance Filters
  const [importanceFilters, setImportanceFilters] = useState({
    管理用: false,
    コア: false,
    補助用: false,
  });
  const [theme, setTheme] = useState("light");

  const [modeInfoPopup, setModeInfoPopup] = useState(null);

  useEffect(() => {
    let cancelled = false;
    http.get(API_ENDPOINTS.OCR_UI_CONFIG)
      .then((response) => {
        if (cancelled) return;
        const config = response?.data?.data || {};
        setUiVisibility({
          showFast: config.showFast === true,
          showImagePreprocessing: config.showImagePreprocessing === true,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setUiVisibility({ showFast: false, showImagePreprocessing: false });
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!uiVisibility.showFast && modeId === "mode_fast") {
      setModeId("mode_standard");
    }
    if (!uiVisibility.showImagePreprocessing && preprocess) {
      setPreprocess(false);
    }
  }, [uiVisibility, modeId, preprocess]);

  const getCreditCostPerPage = (selectedModeId, extractionMode) => {
    if (selectedModeId === "mode_fast") return 1;
    if (selectedModeId === "mode_high_quality") {
      return extractionMode === "template" ? 3 : 5;
    }
    return extractionMode === "template" ? 1 : 2;
  };

  // ✅ Preview Drag & Drop handlers
  const onPreviewDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewDrag(true);
  };

  const onPreviewDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewDrag(false);
  };

  const onPreviewDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewDrag(true);
  };

  const onPreviewDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewDrag(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const [copyPopupOpen, setCopyPopupOpen] = useState(false);
  const [copyMode, setCopyMode] = useState("text");
  const [copySpotRect, setCopySpotRect] = useState(null);

  // edit state
  const [editMode, setEditMode] = useState("normal"); // "normal" | "mask" | "spot"
  const [rectsByPage, setRectsByPage] = useState({});
  const [rectsPx, setRectsPx] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPdfPages, setSelectedPdfPages] = useState([]);
  const selectedPdfPageSet = useMemo(() => new Set(selectedPdfPages), [selectedPdfPages]);
  const [pdfPageCount, setPdfPageCount] = useState(1);
  const [activeDrag, setActiveDrag] = useState(null);

  // templates
  const [useCloudTemplates, setUseCloudTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");

  // ✅ PDF rel rects from PdfMaskPreview
  const [pdfRelRects, setPdfRelRects] = useState([]);

  // IMAGE: wrapper size for rel-rect conversion (CSS size)
  const imageSurfaceRef = useRef(null);
  const [imageSurfaceSize, setImageSurfaceSize] = useState({ w: 0, h: 0 });

  // ✅ IMAGE: natural pixel size (what python needs)
  const imageRef = useRef(null);
  const [imagePixelSize, setImagePixelSize] = useState({ w: 0, h: 0 });

  // ✅ PDF: true canvas pixel size (what python needs)
  const [pdfRenderSize, setPdfRenderSize] = useState({ w: 0, h: 0 });

  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);

  // ✅ FIX: stable objectURL for image preview (prevents endless blob loop)
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);

  const [copyBlob, setCopyBlob] = useState(null);
  const [copyPreviewUrl, setCopyPreviewUrl] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [errorPopup, setErrorPopup] = useState("");
  const [boxReviewMeta, setBoxReviewMeta] = useState(null);
  const [boxReviewLoading, setBoxReviewLoading] = useState(false);
  const [isBoxReviewRoute, setIsBoxReviewRoute] = useState(() => {
    const params = new URLSearchParams(window.location.search || "");
    return params.get("source") === "box";
  });

  // ✅ Refs for PDF/Image capture
  const pdfPreviewRef = useRef(null);

  // ✅ Alignment Preview Polygons
  const [templatePolygons, setTemplatePolygons] = useState([]);
  const [aligning, setAligning] = useState(false);

  const fileType = useMemo(() => getFileType(file), [file]);

  const [popupPos, setPopupPos] = useState({ x: 40, y: 120 });
  const [draggingPopup, setDraggingPopup] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasUnsavedData = useMemo(() => {
    return !!file || !!contextJson || Object.keys(edits || {}).length > 0;
  }, [file, contextJson, edits]);

  const [navPopup, setNavPopup] = useState(false);
  const isBoxReview = isBoxReviewRoute || Boolean(boxReviewMeta);

  const allowNavigation = useRef(false);
  const skipNextPop = useRef(false);
  const historyInitialized = useRef(false);
  const uploadErrorTimer = useRef(null);
  const loadingBoxReviewRef = useRef(false);
  const skipNextFileResetRef = useRef(false);

  const [selectedModel, setSelectedModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [latencyMs, setLatencyMs] = useState(null);


  useEffect(() => {
    (async () => {
      try {
        const res = await http.get(API_ENDPOINTS.MODELS);

        setAvailableModels(res.data.models || []);
        setSelectedModel(res.data.default || "");
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const startPopupDrag = (e) => {
    e.preventDefault();   // 🔥 important

    setDraggingPopup(true);

    dragOffset.current = {
      x: e.clientX - popupPos.x,
      y: e.clientY - popupPos.y
    };
  };

  const handlePageChange = (newPage) => {

    setRectsByPage(prev => {

      // save current page rectangles
      const updated = {
        ...prev,
        [currentPage]: rectsPx
      };

      // load rectangles for the next page
      const nextRects = updated[newPage] || [];

      setRectsPx(nextRects);

      return updated;
    });

    setCurrentPage(newPage);
  };

  const movePopup = (e) => {
    if (!draggingPopup) return;

    setPopupPos({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y
    });
  };

  const stopPopupDrag = () => {
    setDraggingPopup(false);
  };

  useEffect(() => {
    if (!historyInitialized.current) {
      window.history.pushState(null, "", window.location.href);
      historyInitialized.current = true;
    }

    const handlePopState = () => {

      if (allowNavigation.current) {
        return;
      }

      if (hasUnsavedData) {
        setNavPopup(true);

        // push only one blocking state
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || "");

    if (params.get("source") !== "box") {
      setIsBoxReviewRoute(false);
      return;
    }

    setIsBoxReviewRoute(true);

    const resultFileId = params.get("resultFileId");
    const extractionId = params.get("extractionId");

    if (!resultFileId && !extractionId) {
      setError("Box resultFileId or extractionId is missing.");
      return;
    }

    let cancelled = false;

    const loadBoxResult = async () => {
      try {
        loadingBoxReviewRef.current = true;
        setBoxReviewLoading(true);
        setError(null);

        const res = await http.get("/api/box/result", {
          params: extractionId ? { extractionId } : { resultFileId },
        });

        if (cancelled) return;

        const saved = res.data?.data;

        if (!res.data?.success || !saved) {
          throw new Error(res.data?.error || "Failed to load Box result.");
        }

        const rawContext =
          saved?.ocrResult?.data?.context ||
          saved?.ocrResult?.context ||
          saved?.data?.context ||
          saved?.context ||
          saved;

        setModeId(getModeIdFromSavedBoxResult(saved));
        if (saved.templateName) {
          setUseCloudTemplates(true);
          setSelectedTemplateName(saved.templateName);
          setSaveTemplateName(saved.templateName);
        }

        if (extractionId) {
          try {
            const sourceRes = await http.get("/api/box/source", {
              params: { extractionId },
              responseType: "blob",
            });
            const sourceFile = new File(
              [sourceRes.data],
              saved.fileName || "box-source.pdf",
              { type: "application/pdf" }
            );
            skipNextFileResetRef.current = true;
            setFile(sourceFile);
            setPreviewZoom(1);
          } catch (sourceError) {
            console.error("Box source PDF load failed:", sourceError);
          }
        } else {
          setFile(null);
        }

        setContextJson(normalizeExtractContext(rawContext));
        setEdits({});
        setBoxReviewMeta({
          resultFileId,
          extractionId,
          fileName: saved.fileName,
          documentType: saved.documentType,
          templateName: saved.templateName,
          plan: saved.plan,
          processedAt: saved.processedAt,
        });
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || e?.message || "Failed to load Box result.");
        }
      } finally {
        if (!cancelled) {
          setBoxReviewLoading(false);
          setTimeout(() => {
            loadingBoxReviewRef.current = false;
          }, 0);
        }
      }
    };

    loadBoxResult();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (allowNavigation.current) return;
      if (!hasUnsavedData) return;

      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedData]);

  // ✅ Align Template Masks whenever template or file changes
  useEffect(() => {
    if (!useCloudTemplates || !selectedTemplateName || !file) {
      setTemplatePolygons([]);
      return;
    }

    const fetchAlignment = async () => {
      try {
        setAligning(true);
        const pages = await getPageCount(file);

        const rw = fileType === "image" ? imagePixelSize.w : pdfRenderSize.w;
        const rh = fileType === "image" ? imagePixelSize.h : pdfRenderSize.h;

        // If sizes aren't ready yet, skip and wait for next trigger
        if (rw <= 0 || rh <= 0) return;

        const res = await ocrAlign({
          file,
          templateName: selectedTemplateName,
          numberOfPages: pages,
          renderWidth: rw,
          renderHeight: rh,
        });

        if (res?.success && res.polygons) {
          setTemplatePolygons(res.polygons);
        } else {
          setTemplatePolygons([]);
        }
      } catch (err) {
        console.error("Alignment fetch error:", err);
        setTemplatePolygons([]);
      } finally {
        setAligning(false);
      }
    };

    fetchAlignment();
  }, [useCloudTemplates, selectedTemplateName, file, fileType, imagePixelSize, pdfRenderSize]);

  useEffect(() => {
    window.addEventListener("mousemove", movePopup);
    window.addEventListener("mouseup", stopPopupDrag);

    return () => {
      window.removeEventListener("mousemove", movePopup);
      window.removeEventListener("mouseup", stopPopupDrag);
    };
  }, [draggingPopup]);

  useEffect(() => {
    if (!copyBlob) {
      setCopyPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(copyBlob);
    setCopyPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [copyBlob]);

  useEffect(() => {
    document.body.classList.add("ocr-active");
    document.body.classList.remove("ocr-light", "ocr-dark");
    document.body.classList.add(`ocr-${theme}`);

    return () => {
      document.body.classList.remove("ocr-active", "ocr-light", "ocr-dark");
    };
  }, [theme]);


  // ✅ STEP 1: Auto-fit PDF zoom on mobile
  useEffect(() => {
    if (fileType !== "pdf") return;

    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      setPreviewZoom(1.3); // 👈 makes PDF fill the card
    } else {
      setPreviewZoom(1);
    }
  }, [fileType, file]);


  // ✅ FIX: create/revoke blob URL only when image file changes
  useEffect(() => {
    setImagePreviewUrl(null);

    if (!file || fileType !== "image") return;

    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file, fileType]);

  useEffect(() => {
    if (loadingBoxReviewRef.current || skipNextFileResetRef.current) {
      skipNextFileResetRef.current = false;
      return;
    }

    // reset on file change
    setRectsPx([]);
    setActiveDrag(null);
    setEditMode("normal");
    setPdfRelRects([]);
    setImageSurfaceSize({ w: 0, h: 0 });
    setImagePixelSize({ w: 0, h: 0 });
    setPdfRenderSize({ w: 0, h: 0 });

    // also reset result
    setContextJson(null);
    setEdits({});
    setError(null);
  }, [file]);

  // load templates when toggled ON
  useEffect(() => {
    if (!useCloudTemplates) {
      setTemplates([]);
      setSelectedTemplateName("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await http.get(API_ENDPOINTS.OCR_TEMPLATES);
        const json = res.data;
        if (cancelled) return;
        if (json?.success) {
          setTemplates(json.data?.templates || []);
        } else {
          console.error("Template list failed:", json?.message || json);
          setTemplates([]);
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Template list fetch error:", e);
        setTemplates([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [useCloudTemplates]);

  useEffect(() => {
    if (isBoxReview || !useCloudTemplates || !selectedTemplateName) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await http.get(
          `${API_ENDPOINTS.OCR_TEMPLATES}/${encodeURIComponent(selectedTemplateName)}`
        );
        const json = res.data;
        if (cancelled) return;

        if (json?.success) {
          const template = json.data?.template || {};
          let body = template.template_body || {};
          if (template.template_json_str) {
            try {
              body = JSON.parse(template.template_json_str);
            } catch (parseError) {
              console.warn("Stored template JSON string is invalid; using template body.", parseError);
            }
          }
          const structureOnlyBody = clearTemplateExtractedValues(body);
          setContextJson(normalizeExtractContext(structureOnlyBody));
          setEdits({});
          setSaveTemplateName(json.data?.name || selectedTemplateName);
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Template detail fetch error:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isBoxReview, useCloudTemplates, selectedTemplateName]);

  useEffect(() => {
    const handleHomeClick = () => {

      if (!hasUnsavedData) {
        window.location.href = "/";
        return;
      }

      window.pendingNavigation = "/";
      setNavPopup(true);
    };

    window.addEventListener("APP_HOME_CLICK", handleHomeClick);

    return () => {
      window.removeEventListener("APP_HOME_CLICK", handleHomeClick);
    };
  }, [hasUnsavedData]);

  // measure image surface (CSS) whenever zoom/file changes
  useEffect(() => {
    if (fileType !== "image") return;
    const el = imageSurfaceRef.current;
    if (!el) return;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setImageSurfaceSize({ w: r.width, h: r.height });
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [fileType, file, previewZoom]);

  // image drag handlers
  const onImageMouseDown = (e) => {
    if (editMode === "normal") return;
    if (fileType !== "image") return;

    const el = imageSurfaceRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    const x = e.clientX - r.left;
    const y = e.clientY - r.top;

    setActiveDrag({ startX: x, startY: y, curX: x, curY: y });
  };

  const onCopyOcr = async () => {
    if (!copyBlob) return;

    try {
      setCopyLoading(true);
      setCopySuccess(false);

      // 🔹 Check user credits first
      const meRes = await http.get(API_ENDPOINTS.ME);
      const license = meRes.data?.data?.license || {};
      const userCredits = Number(license.creditsRemaining || 0);

      if (userCredits < 1) {
        setCreditPopup({
          required: 1,
          remaining: userCredits
        });
        setCopyLoading(false);
        return;
      }

      const file = new File([copyBlob], "crop.png", {
        type: "image/png",
      });

      const res = await ocrCopy({
        file,
        renderWidth: pdfRenderSize.w || imagePixelSize.w,
        renderHeight: pdfRenderSize.h || imagePixelSize.h,
        spotCropRect: null,
      });

      emitCreditUpdated();

      if (!res?.text) return;

      if (copyMode === "text") {
        await navigator.clipboard.writeText(res.text);

        setCopySuccess(true);

        setTimeout(() => {
          setCopySuccess(false);
        }, 3000);
      }

      if (copyMode === "table") {
        downloadCsv(res.text);
      }

    } catch (err) {
      console.error(err);
    } finally {
      setCopyLoading(false);
    }
  };
  const onImageMouseMove = (e) => {
    if (!activeDrag) return;
    const el = imageSurfaceRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    const x = e.clientX - r.left;
    const y = e.clientY - r.top;

    setActiveDrag((d) => (d ? { ...d, curX: x, curY: y } : d));
  };

  const onImageMouseUp = () => {
    if (!activeDrag) return;

    const x = Math.min(activeDrag.startX, activeDrag.curX);
    const y = Math.min(activeDrag.startY, activeDrag.curY);
    const w = Math.abs(activeDrag.curX - activeDrag.startX);
    const h = Math.abs(activeDrag.curY - activeDrag.startY);

    setActiveDrag(null);
    if (w < 2 || h < 2) return;

    if (editMode === "spot") {
      const relX = x / imageSurfaceSize.w;
      const relY = y / imageSurfaceSize.h;
      const relW = w / imageSurfaceSize.w;
      const relH = h / imageSurfaceSize.h;

      setRectsPx([{ x: relX, y: relY, w: relW, h: relH, isRelative: true }]);
      setCopySpotRect({ x, y, w, h });

      // ✅ Generate Crop Blob for Images
      const img = imageRef.current;
      if (img && imageSurfaceSize.w > 0) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const scaleX = img.naturalWidth / imageSurfaceSize.w;
        const scaleY = img.naturalHeight / imageSurfaceSize.h;

        const sx = x * scaleX;
        const sy = y * scaleY;
        const sw = w * scaleX;
        const sh = h * scaleY;

        canvas.width = sw;
        canvas.height = sh;
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

        canvas.toBlob((blob) => {
          setCopyBlob(blob);
        }, "image/png");
      }

      setCopyPopupOpen(true);
      return;
    }

    if (editMode === "mask") {
      const relX = x / imageSurfaceSize.w;
      const relY = y / imageSurfaceSize.h;
      const relW = w / imageSurfaceSize.w;
      const relH = h / imageSurfaceSize.h;

      const newRect = { x: relX, y: relY, w: relW, h: relH, isRelative: true };
      setRectsByPage((prev) => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), newRect],
      }));
      return;
    }
  };

  const clearRects = () => {

    // clear masks for current page
    setRectsByPage(prev => ({
      ...prev,
      [currentPage]: []
    }));

    // clear UI rectangles
    setRectsPx([]);

    setActiveDrag(null);
    setPdfRelRects([]);
    setCopySpotRect(null);
    setCopyPopupOpen(false);

    // turn off edit mode
    setEditMode("normal");
  };
  const handleFileSelect = async (f) => {
    if (!f) return;

    setUploadError("");

    if (f.type === "application/pdf") {
      const pages = await getPageCount(f);
      setPdfPageCount(pages);
      setCurrentPage(1);
      setSelectedPdfPages(pages > 5 ? [1, 2, 3, 4, 5] : []);
    }
    if (uploadErrorTimer.current) {
      clearTimeout(uploadErrorTimer.current);
    }
    if (f.type !== "application/pdf") {
      setPdfPageCount(1);
      setCurrentPage(1);
      setSelectedPdfPages([]);
    }
    setFile(f);
    setPreviewZoom(1);
  };

  const onExtract = async () => {
    if (!file) return;

    setError(null);
    setLoading(true);
    setContextJson(null);
    setEdits({});

    try {
      const pages = await getPageCount(file);
      const pageNumbers =
        fileType === "pdf" && selectedPdfPages.length > 0
          ? [...selectedPdfPages].sort((a, b) => a - b)
          : null;
      const extractionPageCount = pageNumbers ? pageNumbers.length : pages;

      if (fileType === "pdf" && extractionPageCount > 5) {
        setUploadError(text("PDFの抽出は最大5ページまで可能です", "PDF extraction supports up to 5 pages."));
        if (uploadErrorTimer.current) {
          clearTimeout(uploadErrorTimer.current);
        }
        uploadErrorTimer.current = setTimeout(() => {
          setUploadError("");
        }, 5000);
        setLoading(false);
        return;
      }
      const extractionMode =
        modeId !== "mode_fast" && useCloudTemplates && selectedTemplateName
          ? "template"
          : "full";
      const costPerPage = getCreditCostPerPage(modeId, extractionMode);
      const requiredCredits = extractionPageCount * costPerPage;

      const meRes = await http.get(API_ENDPOINTS.ME);
      const license = meRes.data?.data?.license || {};
      const userCredits = Number(license.creditsRemaining || 0);

      console.log("User credits:", userCredits);
      console.log("Required credits:", requiredCredits);

      if (userCredits < requiredCredits) {
        setCreditPopup({
          required: requiredCredits,
          remaining: userCredits
        });
        setLoading(false);
        return;
      }

      let relRects = [];

      // ✅ Collect masks from ALL pages
      const allPages = {
        ...rectsByPage,
        [currentPage]: rectsPx
      };

      Object.entries(allPages).forEach(([page, rects]) => {
        if (!Array.isArray(rects)) return;
        rects.forEach(r => {
          const rr = toRelRect(
            r,
            fileType === "pdf" ? pdfRenderSize.w : imageSurfaceSize.w,
            fileType === "pdf" ? pdfRenderSize.h : imageSurfaceSize.h
          );
          if (rr) {
            relRects.push({
              page: Number(page),
              ...rr
            });
          }
        });
      });
      const manualMaskRects =
        editMode !== "spot" && relRects.length ? relRects : [];

      const spotCropRect =
        editMode === "spot" && relRects.length ? relRects[0] : null;

      // true render size in pixels
      let renderWidth = 0;
      let renderHeight = 0;

      if (fileType === "image") {
        renderWidth = imagePixelSize.w;
        renderHeight = imagePixelSize.h;
      } else if (fileType === "pdf") {
        renderWidth = pdfRenderSize.w;
        renderHeight = pdfRenderSize.h;
      }

      if (!renderWidth || !renderHeight) {
        throw new Error(
          `renderWidth/renderHeight missing. Got: ${renderWidth}x${renderHeight}. ` +
          `Make sure the preview is rendered (image loaded or PDF canvas rendered) before Extract.`
        );
      }

      const useStreaming =
        extractionMode === "full" &&
        manualMaskRects.length === 0 &&
        !spotCropRect &&
        !pageNumbers;

      if (useStreaming) {
        streamAccumulated.current = "";
        lastStreamBlockCount.current = 0;
        setStreamStatus(text(`${pages}ページを一括でLLMに送信中…`, `Sending ${pages} pages to the extraction service...`));

        const finalEvent = await ocrExtractStream({
          file,
          modeId,
          preprocess,
          numberOfPages: extractionPageCount,
          pageNumbers,
          renderWidth,
          renderHeight,
          onProgress: (message) => setStreamStatus(message || text("抽出中…", "Extracting...")),
          onChunk: (chunk) => {
            streamAccumulated.current += chunk;
            const partial = tryPartialJsonParse(streamAccumulated.current);
            if (!partial) return;
            const blockCount = Object.keys(partial).length;
            if (blockCount > lastStreamBlockCount.current) {
              lastStreamBlockCount.current = blockCount;
              setContextJson(normalizeExtractContext(partial));
              setStreamStatus(text(`${blockCount}セクションを抽出済み…`, `${blockCount} sections extracted...`));
            }
          },
        });

        setContextJson(normalizeExtractContext(finalEvent.data));
        setStreamStatus("");
        emitCreditUpdated();
      } else {
        const extractRes = await ocrExtract({
          file,
          modeId,
          preprocess,
          numberOfPages: extractionPageCount,
          pageNumbers,
          renderWidth,
          renderHeight,
          manualMaskRects,
          spotCropRect,
          templateName: extractionMode === "template" ? selectedTemplateName : "",
          debugReturnSentImage: false,
          template_importance:
            importanceFilters.コア ? "コア" : importanceFilters.管理用 ? "管理用" : importanceFilters.補助用 ? "補助用" : null
        });

        if (extractRes?.success === true) emitCreditUpdated();
        else {
          console.log("OCR failed - process not called (no credit)");
          setError(toUserFacingOcrError(extractRes));
          setContextJson(null);
          return;
        }
        const rawContext = extractRes?.success && extractRes?.data?.context ? extractRes.data.context : extractRes;
        setContextJson(normalizeExtractContext(rawContext));
      }
    } catch (e) {
      setError(toUserFacingOcrError(e?.response?.data || e));
    } finally {
      setLoading(false);
    }
  };

  const onSaveTemplate = async () => {
    if (!contextJson) {
      setError(text("先に抽出を実行して、保存するJSONを生成してください", "Run extraction first to generate the JSON to save."));
      return;
    }

    const name = String(saveTemplateName || selectedTemplateName || "").trim();
    if (!name) {
      setError(text("テンプレート名を入力してください", "Enter a template name."));
      return;
    }

    const anyFilterSelected =
      importanceFilters.管理用 ||
      importanceFilters.コア ||
      importanceFilters.補助用;

    if (!anyFilterSelected) {
      setError(text("保存対象を選択してください（管理用・コア・補助用）", "Select at least one field group to save (Administrative, Core, or Supplementary)."));
      return;
    }

    try {
      setSavingTemplate(true);
      setError(null);

      // ✅ Step 1: Apply edits (ALWAYS full JSON)
      const fullJson = applyEditsToJson(
        structuredClone(contextJson),
        edits
      );

      // Save only the extraction categories explicitly selected by the user.
      const jsonToSave = clearTemplateExtractedValues(
        filterTemplateSchemaByImportance(fullJson, importanceFilters) || {}
      );
      // ✅ Step 2: Prepare mask rects (if any)
      let relRects = [];

      if (fileType === "pdf") {

        // include masks from current page
        const allPages = {
          ...rectsByPage,
          [currentPage]: rectsPx
        };

        Object.entries(allPages).forEach(([page, rects]) => {

          rects.forEach(r => {

            const rr = toRelRect(
              r,
              pdfRenderSize.w,
              pdfRenderSize.h
            );

            if (rr) {
              relRects.push({
                page: Number(page),
                ...rr
              });
            }

          });

        });

      } else {

        relRects = rectsPx
          .map(r => toRelRect(r, imageSurfaceSize.w, imageSurfaceSize.h))
          .filter(Boolean);

      }

      // ✅ Step 3: Create payload
      const payload = {
        templateName: name,

        template_body: jsonToSave,
        template_json_str: JSON.stringify(jsonToSave),

        created_with_plan:
          modeId === "mode_high_quality"
            ? "Premium"
            : modeId === "mode_fast"
              ? "Fast"
              : "Standard",

        mask_rects: editMode === "mask" ? relRects : [],
        reference_image: await (async () => {
          if (fileType === "pdf") {
            return pdfPreviewRef.current?.getCanvasImage() || null;
          } else if (fileType === "image" && file) {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result.split(",")[1]);
              reader.onerror = () => resolve(null);
              reader.readAsDataURL(file);
            });
          }
          return null;
        })(),
      };

      // ✅ Step 4: Send to backend
      const res = selectedTemplateName
        ? await http.patch(
          `${API_ENDPOINTS.OCR_TEMPLATES}/${encodeURIComponent(selectedTemplateName)}`,
          payload
        )
        : await http.post(API_ENDPOINTS.OCR_TEMPLATES, payload);

      const json = res.data;
      if (!json?.success) {
        throw new Error(json?.message || "Template save failed");
      }

      const savedName = json?.data?.name || name;

      // Reset edits after save
      setEdits({});

      // ✅ Refresh template list if cloud enabled
      if (useCloudTemplates) {
        const listRes = await http.get(API_ENDPOINTS.OCR_TEMPLATES);
        const listJson = listRes.data;

        if (listJson?.success) {
          setTemplates(listJson.data?.templates || []);
        }

        setSelectedTemplateName(savedName);
      }

      // ✅ UI feedback
      setSaveTemplateName(savedName);
      setSaveMsg(selectedTemplateName ? text("更新しました ✓", "Updated ✓") : text("保存しました ✓", "Saved ✓"));

      setTimeout(() => setSaveMsg(""), 5000);
    } catch (e) {
      setError(e?.message || "Template save failed");
    } finally {
      setSavingTemplate(false);
    }
  };


  const onDeleteTemplate = async () => {
    const name = String(selectedTemplateName || "").trim();
    if (!name) {
      setError(text("削除するテンプレートを選択してください", "Select a template to delete."));
      return;
    }

    const ok = window.confirm(text(`テンプレート「${name}」を削除しますか？`, `Delete template “${name}”?`));
    if (!ok) return;

    try {
      setDeletingTemplate(true);
      setError(null);

      const res = await http.delete(
        `${API_ENDPOINTS.OCR_TEMPLATES}/${encodeURIComponent(name)}`
      );

      const json = res.data;
      if (!json?.success) {
        throw new Error(json?.message || "Template delete failed");
      }

      const listRes = await http.get(API_ENDPOINTS.OCR_TEMPLATES);
      const listJson = listRes.data;
      setTemplates(listJson?.success ? listJson.data?.templates || [] : []);
      setSelectedTemplateName("");
      setSaveTemplateName("");
      setTemplatePolygons([]);
      setContextJson(null);
      setEdits({});
      setSaveMsg(text("削除しました ✓", "Deleted ✓"));
      setTimeout(() => setSaveMsg(""), 5000);
    } catch (e) {
      setError(e?.message || "Template delete failed");
    } finally {
      setDeletingTemplate(false);
    }
  };


  return (
    <div
      className={`ocr-page theme-${theme} ${expandPreview ? "expand-preview" : ""
        } ${expandResult ? "expand-result" : ""}`}
    >
      <div id="tutorial-processing-plans" className="ocr-page-topbar">
        <span className="ocr-label">{text("処理プラン", "Processing plan")}</span>

        {[
          { label: "Standard", value: "mode_standard" },
          { label: "Premium", value: "mode_high_quality" },
          ...(uiVisibility.showFast
            ? [{ label: "Fast", value: "mode_fast" }]
            : []),
        ].map((m) => (
          <label key={m.value} className="ocr-radio">
            <input
              type="radio"
              checked={modeId === m.value}
              onChange={() => {
                const newMode = m.value;

                // convert mode → label
                const newPlan =
                  newMode === "mode_high_quality"
                    ? "Premium"
                    : newMode === "mode_fast"
                      ? "Fast"
                      : "Standard";

                if (useCloudTemplates && selectedTemplateName) {
                  const selectedTemplate = templates.find(
                    (t) => t.name === selectedTemplateName
                  );

                  const templatePlan = selectedTemplate?.created_with_plan;

                  if (templatePlan && templatePlan !== newPlan) {
                    setModeInfoPopup({
                      templatePlan,
                      currentPlan: newPlan,
                    });
                  }
                }

                if (newMode === "mode_fast") {
                  setUseCloudTemplates(false);
                  setSelectedTemplateName("");
                  setTemplates([]);
                  setTemplatePolygons([]);
                  setSaveTemplateName("");
                }

                setModeId(newMode);
              }}
            />
            {m.label}
          </label>
        ))}

        {uiVisibility.showImagePreprocessing && (
        <div className="ocr-preprocess-control">
          <label className="ocr-checkbox">
            <input
              type="checkbox"
              checked={preprocess}
              onChange={(e) => setPreprocess(!!e?.target?.checked)}
            />
            {text("画像補正", "Image enhancement")}
          </label>
          <button
            type="button"
            className="ocr-help-tooltip"
            aria-label={text("画像補正の説明", "About image enhancement")}
            aria-describedby="ocr-preprocess-tooltip"
          >
            i
            <span id="ocr-preprocess-tooltip" role="tooltip">
              {text("文字を読み取りやすい白黒画像に自動調整します。スキャン文書におすすめです。", "Automatically adjusts the document to high-contrast black and white for easier text recognition. Recommended for scanned documents.")}
            </span>
          </button>
        </div>
        )}

        <div className="ocr-theme-toggle">
          <button
            type="button"
            className="ocr-btn ghost"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
          >
            {theme === "dark" ? "☀ Light" : "🌙 Dark"}
          </button>
        </div>
      </div>

      {isBoxReview && (
        <div
          className="ocr-card"
          style={{
            marginBottom: 16,
            padding: "14px 18px",
            borderColor: "#bfdbfe",
            background: "#eff6ff",
          }}
        >
          <strong>{text("Box保存済み抽出結果", "Saved Box extraction result")}</strong>
          <div style={{ color: "#475569", fontSize: 14, marginTop: 4 }}>
            {text("Boxで抽出済みの保存データを表示しています。OCRは再実行されないため、クレジットは消費されません。", "This is a saved extraction result from Box. OCR is not run again, so no credits are consumed.")}
            {boxReviewMeta?.fileName ? ` ${text("ファイル", "File")}: ${boxReviewMeta.fileName}` : ""}
            {boxReviewMeta?.plan ? ` / ${text("プラン", "Plan")}: ${boxReviewMeta.plan}` : ""}
          </div>
        </div>
      )}

      {boxReviewLoading && (
        <div className="ocr-card" style={{ marginBottom: 16, padding: "14px 18px" }}>
          {text("Boxの保存済み抽出結果を読み込んでいます...", "Loading the saved Box extraction result...")}
        </div>
      )}

      <div className="ocr-layout">
        {!expandResult && (
          <div className="ocr-card">
            {aligning && (
              <div className="ocr-loading-overlay">
                <div className="ocr-spinner"></div>
                <p>{text("位置合わせ中...", "Aligning document...")}</p>
              </div>
            )}
            <div className="ocr-header">
              <h3>{text("1. 文書のアップロード", "1. Upload document")}</h3>
              {file && (
                <button
                  type="button"
                  className="ocr-btn ghost"
                  onClick={() => setExpandPreview((v) => !v)}
                >
                  {expandPreview ? text("⤡ 縮小", "⤡ Collapse") : text("⤢ 拡大", "⤢ Expand")}
                </button>
              )}
            </div>

            {modeId !== "mode_fast" && (
            <div id="tutorial-templates" style={{ marginTop: 10 }}>
              <div className="ocr-muted" style={{ marginBottom: 6 }}>
                📘 {text("テンプレート適用", "Apply template")}
              </div>

              <label
                className="ocr-checkbox"
                style={{ display: "flex", gap: 8, alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  checked={useCloudTemplates}
                  onChange={(e) => {
                    const enabled = Boolean(e?.target?.checked);

                    if (!enabled) {
                      // Sheet 29: the result panel may currently contain the
                      // structure-only body loaded from the selected template.
                      // Removing template mode must remove that stale schema too.
                      setContextJson(null);
                      setEdits({});
                      setSaveTemplateName("");
                      setSaveMsg("");
                      setError(null);
                    }

                    setUseCloudTemplates(enabled);
                  }}
                />
                {text("テンプレートを読み込む", "Load template")}
              </label>

              {useCloudTemplates && (
                <select
                  value={selectedTemplateName}
                  onChange={(e) => {
                    const newTemplateName = e.target.value;
                    setSelectedTemplateName(newTemplateName);

                    if (!newTemplateName) return;

                    const selectedTemplate = templates.find(
                      (t) => t.name === newTemplateName
                    );

                    const templatePlanRaw = selectedTemplate?.created_with_plan;

                    const templatePlan = templatePlanRaw
                      ? templatePlanRaw.trim().toLowerCase()
                      : null;

                    const currentPlan =
                      modeId === "mode_high_quality"
                        ? "Premium"
                        : modeId === "mode_fast"
                          ? "Fast"
                          : "Standard";

                    const currentPlanNormalized = currentPlan.toLowerCase();

                    console.log("Template:", templatePlan);
                    console.log("Current:", currentPlanNormalized);

                    if (templatePlan && templatePlan !== currentPlanNormalized) {
                      setModeInfoPopup({
                        templatePlan: templatePlanRaw,
                        currentPlan: currentPlan,
                      });
                    }
                  }}
                  style={{
                    width: "100%",
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid rgba(0,0,0,0.12)",
                  }}
                >
                  <option value="">{text("（テンプレートを使用しない）", "(Do not use a template)")}</option>
                  {templates.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.name} {t.created_with_plan ? `(${t.created_with_plan})` : ""}
                      {t.has_mask ? " 🛡️" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            )}

            <div id="tutorial-edit-modes" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 10 }}>
              <span className="ocr-muted">{text("編集モード:", "Edit mode:")}</span>

              <button
                type="button"
                className={`ocr-btn ghost ${editMode === "mask" ? "active" : ""}`}
                onClick={() => setEditMode("mask")}
                disabled={!file || (fileType !== "image" && fileType !== "pdf")}
              >
                {text("マスキング", "Masking")}
              </button>
              <button
                type="button"
                className={`ocr-btn ghost ${editMode === "spot" ? "active" : ""}`}
                onClick={() => setEditMode("spot")}
                disabled={!file || (fileType !== "image" && fileType !== "pdf")}
              >
                {text("スポットコピー", "Spot copy")}
              </button>
              <button
                type="button"
                className="ocr-btn ghost"
                onClick={clearRects}
                disabled={!rectsPx.length && editMode === "normal"}              >
                {text("クリア", "Clear")}
              </button>
            </div>

            {file && fileType === "pdf" && (
              <fieldset className="ocr-page-scope">
                <legend>{text("抽出対象ページ（複数選択可）", "Pages to extract (multiple selections allowed)")}</legend>
                <label className="ocr-page-option">
                  <input
                    type="checkbox"
                    checked={selectedPdfPages.length === 0}
                    onChange={() => {
                      if (pdfPageCount > 5) {
                        setErrorPopup(text("PDFが6ページ以上あるため、全ページの選択はできません。個別に5ページ以内で選択してください。", "This PDF has more than 5 pages. Select up to 5 individual pages."));
                        return;
                      }
                      setSelectedPdfPages([]);
                    }}
                  />
                  {text("全ページ", "All pages")}
                </label>
                {Array.from({ length: pdfPageCount }, (_, index) => index + 1).map((page) => (
                  <label key={page} className="ocr-page-option">
                    <input
                      type="checkbox"
                      checked={selectedPdfPageSet.has(page)}
                      onChange={(event) => {
                        const isChecked = event.target.checked;
                        if (isChecked && selectedPdfPages.length >= 5 && selectedPdfPages.length !== 0) {
                          setErrorPopup(text("最大5ページまでしか選択できません", "You can select up to 5 pages."));
                          return;
                        }
                        setSelectedPdfPages((current) => {
                          const next = new Set(current);
                          if (isChecked) next.add(page);
                          else next.delete(page);
                          return [...next].sort((a, b) => a - b);
                        });
                        if (isChecked) setCurrentPage(page);
                      }}
                    />
                    {text(`${page}ページ`, `Page ${page}`)}
                  </label>
                ))}
                <span className="ocr-muted ocr-page-scope-note">
                  {selectedPdfPages.length === 0
                    ? text("全" + pdfPageCount + "ページを抽出・課金します。", `All ${pdfPageCount} pages will be extracted and charged.`)
                    : text(selectedPdfPages.join("、") + "ページのみ抽出・課金します。", `Only pages ${selectedPdfPages.join(", ")} will be extracted and charged.`)}
                </span>
              </fieldset>
            )}

            {uploadError && (
              <div className="upload-error">
                {uploadError}
              </div>
            )}
            <div className="ocr-preview">

              {/* ✅ Hidden File Input (Browse Button needs this!) */}
              <input
                id="ocr-file-input"
                type="file"
                accept=".png,.jpg,.jpeg,.pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleFileSelect(e.target.files[0]);
                  }

                  // ⭐ reset input so same file can trigger again
                  e.target.value = null;
                }}
              />

              {/* ============================= */}
              {/* Toolbar */}
              {/* ============================= */}
              <div className="ocr-preview-toolbar">
                <button
                  type="button"
                  className="ocr-zoom-btn"
                  onClick={() =>
                    setPreviewZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))
                  }
                  disabled={!file}
                >
                  −
                </button>

                <input
                  className="ocr-zoom-range"
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.05"
                  value={previewZoom}
                  onChange={(e) => setPreviewZoom(parseFloat(e.target.value))}
                  disabled={!file}
                />

                <div className="ocr-zoom-label">
                  {Math.round(previewZoom * 100)}%
                </div>

                <button
                  type="button"
                  className="ocr-zoom-btn"
                  onClick={() =>
                    setPreviewZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))
                  }
                  disabled={!file}
                >
                  +
                </button>

                <button
                  type="button"
                  className="ocr-zoom-reset"
                  onClick={() => setPreviewZoom(1)}
                  disabled={!file}
                >
                  100%
                </button>
              </div>

              {/* ============================= */}
              {/* Preview Drop Zone */}
              {/* ============================= */}
              <div
                id="tutorial-upload-dropzone"
                className={`ocr-preview-viewport 
                ${previewDrag ? "drag-active" : ""} 
                ${uploadError ? "upload-error-box" : ""}`}
                style={{ userSelect: "none" }}
                onDragEnter={onPreviewDragEnter}
                onDragLeave={onPreviewDragLeave}
                onDragOver={onPreviewDragOver}
                onDrop={onPreviewDrop}
              >
                {/* ============================= */}
                {/* EMPTY STATE */}
                {/* ============================= */}
                {!file && (
                  <div className="preview-drop-hint">
                    <div className="preview-icon">☁️</div>

                    <div className="preview-text">
                      Drag & Drop files here
                    </div>

                    <div className="preview-subtext">
                      Supported formats: PDF, JPG, PNG (Max 5 pages extraction for PDF)
                    </div>

                    {/* ✅ Browse Button */}
                    <label
                      className="preview-browse-btn"
                      htmlFor="ocr-file-input"
                    >
                      Browse Files
                    </label>
                  </div>
                )}

                {/* ============================= */}
                {/* IMAGE PREVIEW */}
                {/* ============================= */}
                {file && fileType === "image" && (
                  <div
                    ref={imageSurfaceRef}
                    style={{
                      position: "relative",
                      width: "fit-content",
                      margin: "0 auto",
                      cursor: editMode === "normal" ? "default" : "crosshair",
                    }}
                    onMouseDown={onImageMouseDown}
                    onMouseMove={onImageMouseMove}
                    onMouseUp={onImageMouseUp}
                  >
                    <img
                      ref={imageRef}
                      src={imagePreviewUrl || ""}
                      alt="preview"
                      draggable={false}
                      style={{
                        width: `${800 * previewZoom}px`,
                        height: "auto",
                        display: "block",
                        borderRadius: "10px",
                        border: "1px solid var(--img-border)",
                        boxShadow: "0 14px 40px var(--shadow-img)",
                      }}
                      onLoad={() => {
                        const img = imageRef.current;
                        if (img) {
                          setImagePixelSize({
                            w: img.naturalWidth || 0,
                            h: img.naturalHeight || 0,
                          });
                        }
                        // Update surface size after render
                        setTimeout(() => {
                          const r = imageSurfaceRef.current?.getBoundingClientRect();
                          if (r) setImageSurfaceSize({ w: r.width, h: r.height });
                        }, 50);
                      }}
                    />

                    {/* ✅ UNIFIED SVG LAYER (Manual Masks + Template Masks) */}
                    {fileType === "image" && (
                      <svg
                        viewBox="0 0 1 1"
                        preserveAspectRatio="none"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          pointerEvents: "none",
                          zIndex: 10,
                        }}
                      >
                        {/* 1. Manual Masks */}
                        {(rectsByPage[currentPage] || []).map((r, idx) => (
                          <rect
                            key={`manual-${idx}`}
                            x={r.x}
                            y={r.y}
                            width={r.w}
                            height={r.h}
                            fill="#000"
                          />
                        ))}

                        {/* 2. Template Aligned Polygons */}
                        {templatePolygons
                          .filter(poly => !poly.page || poly.page === 1)
                          .map((poly, idx) => {
                            const points = poly.points
                              .map(p => `${p.rel_x},${p.rel_y}`)
                              .join(" ");
                            return (
                              <polygon
                                key={`temp-${idx}`}
                                points={points}
                                fill="#000"
                                stroke="#000"
                                strokeWidth="0.001"
                              />
                            );
                          })}

                        {/* 3. Spot Selection feedback (temporary) */}
                        {rectsPx.map((r, idx) => (
                          <rect
                            key={`spot-${idx}`}
                            x={r.x}
                            y={r.y}
                            width={r.w}
                            height={r.h}
                            fill="rgba(255,0,0,0.25)"
                            stroke="#f00"
                            strokeWidth="0.005"
                          />
                        ))}
                      </svg>
                    )}

                    {/* Active Drag Rectangle */}
                    {activeDrag && (
                      <div
                        style={{
                          position: "absolute",
                          left: Math.min(
                            activeDrag.startX,
                            activeDrag.curX
                          ),
                          top: Math.min(
                            activeDrag.startY,
                            activeDrag.curY
                          ),
                          width: Math.abs(
                            activeDrag.curX - activeDrag.startX
                          ),
                          height: Math.abs(
                            activeDrag.curY - activeDrag.startY
                          ),
                          border:
                            editMode === "mask"
                              ? "2px dashed #000"
                              : "2px dashed #f00",
                          background:
                            editMode === "mask"
                              ? "rgba(0,0,0,0.35)"
                              : "rgba(255,0,0,0.18)",
                          pointerEvents: "none",
                        }}
                      />
                    )}
                  </div>
                )}

                {/* ============================= */}
                {/* PDF PREVIEW */}
                {/* ============================= */}
                {file && fileType === "pdf" && (
                  <PdfMaskPreview
                    file={file}
                    editMode={editMode}
                    previewZoom={previewZoom}
                    rectsPx={rectsPx}
                    setRectsPx={setRectsPx}
                    activeDrag={activeDrag}
                    setActiveDrag={setActiveDrag}
                    onRelRectsChange={setPdfRelRects}
                    onCanvasSizeChange={setPdfRenderSize}
                    onSpotRectDraw={({ rect, blob }) => {
                      setCopySpotRect(rect);
                      setCopyBlob(blob);
                      setCopyPopupOpen(true);
                    }}
                    onPageChange={handlePageChange}
                    currentPage={currentPage}
                    templatePolygons={templatePolygons}
                    ref={pdfPreviewRef}
                  />
                )}
                {copyPopupOpen &&
                  createPortal(
                    <div
                      className="copy-popup"
                      style={{
                        position: "fixed",
                        top: popupPos.y,
                        left: popupPos.x,
                        width: 420,
                        background: "white",
                        borderRadius: 12,
                        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                        zIndex: 999999
                      }}
                    >

                      {/* 🔹 Drag Header */}
                      <div
                        onMouseDown={(e) => {
                          e.preventDefault();   // 🔥 keeps text selection active
                          startPopupDrag(e);
                        }} style={{
                          padding: "10px 15px",
                          cursor: "move",
                          borderBottom: "1px solid #eee",
                          fontWeight: 600,
                          background: "#f9fafb",
                          borderTopLeftRadius: 12,
                          borderTopRightRadius: 12
                        }}
                      >
                        {text("選択範囲コピー", "Copy selected area")}
                      </div>

                      <div style={{ padding: 20 }}>

                        {/* Cropped Image Preview */}
                        {copyPreviewUrl && (
                          <div style={{ marginBottom: 15, textAlign: "center" }}>
                            <img
                              src={copyPreviewUrl}
                              alt="crop-preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: 200,
                                borderRadius: 8,
                                border: "1px solid #ddd"
                              }}
                            />
                          </div>
                        )}

                        <select
                          value={copyMode}
                          className="copy-select"
                          onChange={(e) => setCopyMode(e.target.value)}
                          style={{
                            width: "100%",
                            padding: 8,
                            marginBottom: 15,
                            borderRadius: 8
                          }}
                        >
                          <option value="text">Text</option>
                          <option value="table">Table</option>
                        </select>

                        <div style={{ display: "flex", gap: 10 }}>
                          <button
                            className="ocr-btn"
                            onClick={onCopyOcr}
                            disabled={copyLoading}
                            style={{
                              flex: 1,
                              opacity: copyLoading ? 0.7 : 1,
                              cursor: copyLoading ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8
                            }}
                          >
                            {copyLoading ? (
                              <>
                                <span className="mini-spinner"></span>
                                {text("処理中...", "Processing...")}
                              </>
                            ) : copySuccess ? (
                              "✅ Copied"
                            ) : copyMode === "table" ? (
                              text("⬇ CSVダウンロード", "⬇ Download CSV")
                            ) : (
                              text("📋 コピー", "📋 Copy")
                            )}
                          </button>

                          <button
                            className="ocr-btn ghost"
                            onClick={() => setCopyPopupOpen(false)}
                            style={{ flex: 1 }}
                          >
                            Close
                          </button>
                        </div>

                      </div>
                    </div>,
                    document.body
                  )
                }
              </div>
            </div>

          </div>
        )}

        {!expandPreview && (
          <div className="ocr-card">
            <div className="ocr-header ocr-header--right">
              {contextJson && (
                <div className="expandbtndiv">

                  <button id="expandbtn" type="button" className="ocr-btn ghost" onClick={() => setExpandResult((v) => !v)}>
                    {expandResult ? text("⤡ 縮小", "⤡ Collapse") : text("⤢ 拡大", "⤢ Expand")}
                  </button>
                </div>)}
              <div className="ocr-actions">
                {!isBoxReview && (
                  <button id="tutorial-extract-execute-btn" type="button" className="ocr-btn danger" disabled={!file || loading} onClick={onExtract}>
                    {loading ? text("抽出中...", "Extracting...") : text("抽出を実行", "Run extraction")}
                  </button>
                )}

                {(
                  <>
                    <button type="button" className="ocr-btn ghost" onClick={() => setShowRaw((v) => !v)}>
                      {showRaw ? "▼ JSON" : "▶ JSON"}
                    </button>

                    <button
                      type="button"
                      className="ocr-btn download-btn"
                      onClick={() => {
                        const anyFilterSelected =
                          importanceFilters.管理用 ||
                          importanceFilters.コア ||
                          importanceFilters.補助用;

                        // The table keeps unsaved value/importance changes in
                        // `edits`. Export the current UI state, not the original
                        // extraction response, then apply the selected filter.
                        const editedContext = applyEditsToJson(
                          structuredClone(contextJson),
                          edits
                        );

                        // Use the schema-preserving filter so the downloaded JSON
                        // exactly matches the viewer, including fields with empty values.
                        const data = anyFilterSelected
                          ? filterTemplateSchemaByImportance(editedContext, importanceFilters)
                          : editedContext;

                        const fileBaseName = sanitizeDownloadBaseName(
                          saveTemplateName || selectedTemplateName || boxReviewMeta?.templateName || "context"
                        );
                        downloadJson(data, `${fileBaseName}.json`);
                      }}
                    >
                      {text("JSONダウンロード", "Download JSON")}
                    </button>

                    <button
                      onClick={() => {
                        const anyFilterSelected =
                          importanceFilters.管理用 ||
                          importanceFilters.コア ||
                          importanceFilters.補助用;

                        // Importance changes are stored separately until a
                        // template is saved. Merge them before filtering/export.
                        const editedContext = applyEditsToJson(
                          structuredClone(contextJson),
                          edits
                        );

                        const data = anyFilterSelected
                          ? filterTemplateSchemaByImportance(editedContext, importanceFilters)
                          : editedContext;

                        const fileBaseName = sanitizeDownloadBaseName(
                          saveTemplateName || selectedTemplateName || boxReviewMeta?.templateName || "context"
                        );
                        downloadCsvFromJson(data, fileBaseName);
                      }}
                      className="ocr-btn download-btn"
                    >
                      {text("CSVダウンロード", "Download CSV")}
                    </button>

                    {modeId !== "mode_fast" && (
                      <>
                        <div className="template-save-step template-save-step--name">
                          <span className="template-save-step-number">1</span>
                          <input
                            id="tutorial-template-save-input"
                            className="template-input"
                            value={saveTemplateName}
                            onChange={(e) => setSaveTemplateName(e.target.value)}
                            placeholder={text("テンプレート名", "Template name")}
                          />
                        </div>

                        <div
                          id="tutorial-importance-filters"
                          className="template-save-step template-save-step--targets"
                        >
                          <span className="template-save-step-number">2</span>
                          <span className="template-save-step-label">{text("保存対象:", "Save fields:")}</span>
                          {IMPORTANCE_LEVELS.map((level) => (
                            <label key={level} className="template-target-option">
                              <input
                                type="checkbox"
                                checked={importanceFilters[level]}
                                onChange={() =>
                                  setImportanceFilters((prev) => ({
                                    ...prev,
                                    [level]: !prev[level],
                                  }))
                                }
                              />
                              {text(level, level === "管理用" ? "Administrative" : level === "コア" ? "Core" : "Supplementary")}
                            </label>
                          ))}
                        </div>

                        <div className="template-save-step template-save-step--actions">
                          <span className="template-save-step-number">3</span>
                          <button
                            className="ocr-btn template-btn template-save-btn"
                            disabled={savingTemplate || deletingTemplate}
                            onClick={onSaveTemplate}
                          >
                            {savingTemplate
                              ? selectedTemplateName
                                ? text("更新中...", "Updating...")
                                : text("保存中...", "Saving...")
                              : selectedTemplateName
                                ? text("テンプレート更新", "Update template")
                                : text("テンプレート保存", "Save template")}
                          </button>

                          {selectedTemplateName && (
                            <button
                              type="button"
                              className="ocr-btn template-btn template-delete-btn"
                              disabled={savingTemplate || deletingTemplate}
                              onClick={onDeleteTemplate}
                            >
                              {deletingTemplate ? text("削除中...", "Deleting...") : text("削除", "Delete")}
                            </button>
                          )}

                          {saveMsg && <span className="ocr-save-msg">{saveMsg}</span>}
                        </div>
                      </>
                    )}


                  </>
                )}
              </div>

              <h2 className="ocr-title">{text("抽出結果のレビューと修正", "Review and edit extraction results")}</h2>
              <div className="ocr-muted ocr-subtitle">{text("抽出された項目を確認・修正してください", "Review and edit the extracted fields.")}</div>
            </div>

            {error && (
              <OcrErrorNotice error={error} onRetry={onExtract} text={text} />
            )}
            {loading && (
              <div className="ocr-inline-loader">
                <div className="magnifier">
                  <div className="glass"></div>
                  <div className="handle"></div>
                </div>
                <div className="loader-text">{streamStatus || text("処理中です…", "Processing...")}</div>
              </div>
            )}

            {showRaw && contextJson && (
              <pre className="ocr-pre">{JSON.stringify(contextJson, null, 2)}</pre>
            )}

            {contextJson && (
              <>
                <StreamlitTableRenderer
                  data={contextJson}
                  setData={setContextJson}
                  edits={edits}
                  setEdits={setEdits}
                />

                {/* <div className="ocr-footer">
                  <button
                    type="button"
                    className="ocr-btn secondary"
                    onClick={() => setContextJson(applyEditsToJson(structuredClone(contextJson), edits))}
                  >
                    修正を反映
                  </button>
                </div> */}
              </>
            )}
          </div>
        )}
      </div>
      {errorPopup && (
        <div className="credit-popup-overlay">
          <div className="credit-popup-card">
            <div className="credit-popup-icon">⚠️</div>
            <h2 className="credit-popup-title">{text("エラー", "Error")}</h2>
            <p className="credit-popup-text">{errorPopup}</p>
            <div className="credit-popup-actions">
              <button className="ocr-btn ghost" onClick={() => setErrorPopup("")}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {creditPopup && (
        <div className="credit-popup-overlay">
          <div className="credit-popup-card">

            <div className="credit-popup-icon">⚠️</div>

            <h2 className="credit-popup-title">
              {text("クレジット不足", "Insufficient credits")}
            </h2>

            <p className="credit-popup-text">
              {text("必要クレジット:", "Credits required:")} <b>{creditPopup.required}</b>
            </p>

            <p className="credit-popup-text">
              {text("残りクレジット:", "Credits remaining:")} <b>{creditPopup.remaining}</b>
            </p>

            <div className="credit-popup-actions">
              <button
                className="ocr-btn ghost"
                onClick={() => setCreditPopup(null)}
              >
                OK
              </button>
            </div>

          </div>
        </div>
      )}
      {navPopup && (
        <div className="credit-popup-overlay">
          <div className="credit-popup-card">

            <div className="credit-popup-icon">⚠️</div>

            <h2 className="credit-popup-title">
              {text("未保存のデータがあります", "You have unsaved data")}
            </h2>

            <p className="credit-popup-text">
              {text("このページには保存されていないOCRデータがあります。", "This page contains OCR data that has not been saved.")}
            </p>

            <p className="credit-popup-text">
              {text("このまま離れるとデータは失われます。", "The data will be lost if you leave this page.")}
            </p>

            <div className="credit-popup-actions">

              <button
                className="ocr-btn ghost"
                onClick={() => setNavPopup(false)}
              >
                {text("キャンセル", "Cancel")}
              </button>

              <button
                className="ocr-btn danger"
                onClick={() => {
                  allowNavigation.current = true;
                  setNavPopup(false);

                  if (window.pendingNavigation) {
                    window.location.href = window.pendingNavigation;
                    window.pendingNavigation = null;
                    return;
                  }

                  window.history.back();
                }}
              >
                {text("ページを離れる", "Leave page")}
              </button>

            </div>
          </div>
        </div>
      )}

      {modeInfoPopup && (
        <div className="credit-popup-overlay">
          <div className="credit-popup-card">

            <div className="credit-popup-icon">ℹ️</div>

            <h2 className="credit-popup-title">
              {text("プラン不一致", "Plan mismatch")}
            </h2>

            <p className="credit-popup-text">
              {text("テンプレート:", "Template:")} <b>{modeInfoPopup.templatePlan}</b>
            </p>

            <p className="credit-popup-text">
              {text("現在のモード:", "Current mode:")} <b>{modeInfoPopup.currentPlan}</b>
            </p>

            <p className="credit-popup-text">
              {text("結果に影響する可能性があります", "This may affect the extraction results.")}
            </p>

            {/* ✅ ADD THIS */}
            <div className="credit-popup-actions">
              <button
                className="ocr-btn ghost"
                onClick={() => setModeInfoPopup(null)}
              >
                OK
              </button>
            </div>

          </div>
        </div>
      )}
    </div >
  );
}

function tryPartialJsonParse(rawText) {
  let text = rawText.trim().replace(/^```json\n?/, "").replace(/^```\n?/, "").replace(/```$/, "").trim();
  const start = text.indexOf("{");
  if (start === -1) return null;
  text = text.slice(start);
  let depth = 0;
  let inString = false;
  let escaped = false;
  let lastGoodEnd = -1;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (escaped) { escaped = false; continue; }
    if (character === "\\" && inString) { escaped = true; continue; }
    if (character === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (character === "{" || character === "[") depth += 1;
    else if (character === "}" || character === "]") { depth -= 1; if (depth === 1) lastGoodEnd = index; }
  }
  if (lastGoodEnd === -1) return null;
  try { return JSON.parse(text.slice(0, lastGoodEnd + 1) + "}"); } catch { return null; }
}
function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadCsv(text) {
  if (!text) return;

  // Add BOM so Excel handles UTF-8 correctly
  const csvContent = "\uFEFF" + text.trim();

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;"
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = "table_extract.csv";

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}
